import React from 'react';
import { ArrowRight, Ticket, Users, DollarSign } from 'lucide-react';
import { useCases } from '../../data/mockData';

const UseCasesSection = ({ onBookDemo }) => {
  const iconMap = {
    'ticket': Ticket,
    'users': Users,
    'dollar-sign': DollarSign
  };

  return (
    <section className="bg-white py-24" id="use-cases" aria-labelledby="usecases-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <header className="mb-16">
          <span className="text-sm font-medium text-purple-600 mb-3 block">Use cases</span>
          <h2 id="usecases-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Start with customer support,{' '}
            <br className="hidden sm:block" />
            <span className="text-gray-400">then scale across use cases</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl">
            Confidently start with support and scale quickly to sales and scheduling.
          </p>
        </header>

        {/* Use Cases Grid */}
        <div className="grid lg:grid-cols-3 gap-8" role="list" aria-label="Use case examples">
          {useCases.map((useCase) => {
            const Icon = iconMap[useCase.icon] || Ticket;
            return (
              <article
                key={useCase.id}
                role="listitem"
                className="group relative rounded-2xl bg-white border border-gray-200 overflow-hidden hover:border-gray-300 hover:shadow-xl transition-all duration-500"
              >
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${useCase.gradient}`} aria-hidden="true" />
                
                {/* Content */}
                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${useCase.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} aria-hidden="true">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  
                  {/* Stat */}
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className={`text-5xl font-bold bg-gradient-to-r ${useCase.gradient} bg-clip-text text-transparent`}>
                      {useCase.stat}
                    </span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {useCase.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-500 leading-relaxed mb-6">
                    {useCase.description}
                  </p>
                  
                  {/* CTA Link */}
                  <button
                    onClick={onBookDemo}
                    className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 group/link"
                    aria-label={`Learn more about ${useCase.title}`}
                  >
                    Learn more
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <button
            onClick={onBookDemo}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors group"
            aria-label="See how AI AutoBots can help your business"
          >
            <span>See how AI AutoBots can help your business</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
